import type { ReactNode } from 'react'

/**
 * Something the person needs told, said where they are looking.
 *
 * Announced when it appears, without moving focus away from the field they are
 * still fixing. `role="alert"` for a problem, because it interrupts; `status`
 * for a confirmation, because it does not.
 *
 * The icon is not decoration. Colour alone cannot carry meaning — roughly one
 * man in twelve cannot reliably separate the red from the surrounding text — so
 * the shape says which of the two this is as well.
 */

export type AlertTone = 'danger' | 'ok' | 'unknown'

const TONES = {
  danger: {
    role: 'alert',
    box: 'border-danger bg-danger-soft',
    text: 'text-danger',
    icon: 'text-danger',
  },
  ok: {
    role: 'status',
    box: 'border-ok bg-ok-soft',
    text: 'text-ok',
    icon: 'text-ok',
  },
  /**
   * "We could not tell." Its own colour, never the danger one. (I7)
   *
   * The same rule the send log and the sending-domain screen follow: an answer
   * the system does not have must not be painted as an answer it does. A test
   * send that timed out, shown in red, sends somebody off to regenerate an API
   * key that was fine.
   *
   * `status` rather than `alert`, because a screen reader should mention it
   * when the person reaches it rather than interrupt: nothing has gone wrong.
   */
  unknown: {
    role: 'status',
    box: 'border-unknown bg-unknown-soft',
    text: 'text-unknown',
    icon: 'text-unknown',
  },
} as const

export function Alert({
  tone = 'danger',
  children,
}: {
  readonly tone?: AlertTone
  readonly children: ReactNode
}) {
  const style = TONES[tone]

  return (
    <div
      role={style.role}
      className={`mb-4 flex items-start gap-2.5 rounded-field border px-3 py-2.5 ${style.box}`}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={`mt-px h-[18px] w-[18px] flex-none ${style.icon}`}
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="9" />
        <path d={tone === 'ok' ? 'M8.5 12.2l2.4 2.4 4.6-4.8' : 'M12 8v4M12 16h.01'} />
      </svg>
      <div className={`text-[13px] ${style.text}`}>{children}</div>
    </div>
  )
}
